import type {RichTextEditorValue as Value} from '@shopify/online-store-ui';
import {
  isDocumentElement,
  isElement,
  isTextNode,
} from '@shopify/online-store-ui';
import isEqual from 'lodash/isEqual';

import type {ShopASTNode} from '../types';
import {EMPTY_VALUE, EMPTY_TEXT_NODE, EMPTY_PARAGRAPH_NODE} from '../constants';

export function deserializeFromShopAST(rootNode: ShopASTNode) {
  return [{children: deserializeASTStructure(rootNode.children, true)}];
}

export function serializeToShopAST(value: Value) {
  if (isEqual(value, EMPTY_VALUE)) {
    return null;
  }

  if (isDocumentElement(value[0]) && 'children' in value[0]) {
    return {
      type: 'root',
      children: serializeASTStructure(value[0].children),
    };
  }

  return {
    type: 'root',
    children: [],
  };
}

export function deserializeASTStructure(
  nodes?: ShopASTNode[],
  isRoot?: boolean,
): Value {
  if (!nodes?.length) {
    return [isRoot ? EMPTY_PARAGRAPH_NODE : EMPTY_TEXT_NODE];
  }

  return nodes.map((node) => {
    if (node.type === 'text') {
      const {type, value, ...marks} = node;

      return {
        text: value,
        ...marks,
      };
    }

    return {
      ...node,
      children: deserializeASTStructure(node.children),
    };
  });
}

export function serializeASTStructure(nodes: Value): ShopASTNode[] {
  return nodes.reduce((shopASTChildren, node) => {
    if (isTextNode(node) && 'text' in node) {
      const {text, ...marks} = node;

      shopASTChildren.push({
        type: 'text',
        value: node.text,
        ...marks,
      } as ShopASTNode);
      return shopASTChildren;
    }

    if (isElement(node)) {
      shopASTChildren.push({
        ...node,
        children: serializeASTStructure(node.children),
      } as ShopASTNode);
      return shopASTChildren;
    }

    if (!('type' in node) && 'children' in node) {
      shopASTChildren.push(...serializeASTStructure(node.children));
      return shopASTChildren;
    }

    return shopASTChildren;
  }, [] as ShopASTNode[]);
}

export function safeParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json);
  } catch (err) {
    return fallback;
  }
}
